import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  inject,
  signal,
} from '@angular/core';
import { Menu, NavService } from '../../services/navservice';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Observable, Subject, filter, takeUntil, catchError, of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../features/auth/services/auth.service';
import { SecureStorageService } from '../../../core/services/secure-storage.service';
import { FormField } from '../../components/forms/dynamic-form/models/dynamic-form.model';
import { SelectComponent } from '../../components/forms/select/select.component';
import { XeroConnectionStatus } from '../../../features/financial-management/xero/models/xero.models';
import { XeroService } from '../../../features/financial-management/xero/services/xero.service';
import { TemplateNotificationListComponent } from '../../../features/notifications/template-notification/components/template-notification-list/template-notification-list.component';
import { UserType } from '../../../core/constants/user-type';
import { CustomDialogService } from '../../../core/services/custom-dialog.service';
import { CompanyLogoService } from '../../../core/services/company-logo.service';
import { CacheService } from '../../../core/services/cache.service';
import { AccountDialogConfigs } from '../../configuration/account-dialog.config';

interface GroupedLocation {
  Id: number;
  Name: string;
  OrganizationName: string;
  OrganizationId: number;
}

interface OrganizationLocationInfo {
  organizationId: number;
  organizationName: string;
  locationId: number;
  locationName: string;
}

const SCROLL_THRESHOLD = 10;
const SELECTED_LOCATION_KEY = 'selectedLocationId';
const SELECTED_ORGANIZATION_KEY = 'selectedOrganizationId';
const FINANCIAL_MANAGEMENT_ROUTE = '/financial-management';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NgbDropdownModule,
    SelectComponent,
    TemplateNotificationListComponent,
  ],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Input() isSidebarCollapsed = false;
  @Output() sidebarToggle = new EventEmitter<void>();

  // Signals
  scrolled = signal(false);

  // State
  isNotifyEmpty = false;
  isFinancialManagementRoute = false;
  xeroConnecting = false;
  searchText = '';

  selectedOrganizationId: number | null = null;
  selectedLocationId: number | null = null;

  // Data
  dropdownConfig!: FormField;
  menuItems!: Menu[];
  items!: Menu[];
  xeroStatus$!: Observable<XeroConnectionStatus>;
  userData: any = {};
  companyLogo: string | null = null;
  organizationList: Array<Record<string, unknown>> = [];

  private readonly destroy$ = new Subject<void>();
  private readonly dialogService = inject(CustomDialogService);
  private readonly companyLogoService = inject(CompanyLogoService);
  private readonly cacheService = inject(CacheService);

  constructor(
    public navServices: NavService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly storageService: SecureStorageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly xeroService: XeroService
  ) {
    this.initializeUserData();
  }

  ngOnInit(): void {
    this.subscribeToNavItems();
    this.subscribeToRouterEvents();
    this.subscribeToUserChanges();
    this.subscribeToCompanyLogo();
    this.getUserData();

    // Defer Xero status to avoid blocking navigation
    setTimeout(() => this.initializeXeroStatus(), 3000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    this.scrolled.set(globalThis.scrollY > SCROLL_THRESHOLD);
  }

  onToggleSidebar(): void {
    this.sidebarToggle.emit();
  }

  onDropdownChange(event: number): void {
    if (this.isFinancialManagementRoute) {
      this.changeLocation(event);
    } else {
      this.changeOrganization(event);
    }
  }

  changeLocation(locationId: number): void {
    this.selectedLocationId = locationId;
    localStorage.setItem(SELECTED_LOCATION_KEY, String(locationId));

    const locationInfo = this.findOrganizationByLocationId(locationId);
    if (!locationInfo) {
      return;
    }

    localStorage.setItem(SELECTED_ORGANIZATION_KEY, String(locationInfo.organizationId));

    this.authService.changeOrganization(locationInfo.organizationId, locationId).subscribe({
      next: (response) => {
        this.handleOrganizationChangeSuccess(response);
      },
      error: () => {
        this.router.navigate(['/login']);
      },
    });
  }

  changeOrganization(organizationId: number): void {
    this.selectedOrganizationId = organizationId;
    localStorage.setItem(SELECTED_ORGANIZATION_KEY, String(organizationId));

    // Clear stale location from previous organization so refreshToken()
    // doesn't send a mismatched organizationId + locationId
    localStorage.removeItem(SELECTED_LOCATION_KEY);
    this.selectedLocationId = null;

    this.authService.changeOrganization(organizationId).subscribe({
      next: (response) => {
        this.handleOrganizationChangeSuccess(response);
      },
      error: () => {
        this.router.navigate(['/login']);
      },
    });
  }

  getUserData(): void {
    this.userData = this.storageService.getUserData();
    if (this.userData) {
      this.selectedOrganizationId = Number(this.userData['organizationId']);

      const storedLocationId = localStorage.getItem(SELECTED_LOCATION_KEY);
      if (storedLocationId) {
        const locationId = Number(storedLocationId);
        // Validate that the stored location belongs to the current organization
        if (this.isLocationInCurrentOrganization(locationId)) {
          this.selectedLocationId = locationId;
        } else {
          localStorage.removeItem(SELECTED_LOCATION_KEY);
          this.selectedLocationId = this.userData['locationId']
            ? Number(this.userData['locationId'])
            : null;
        }
      } else if (this.userData['locationId']) {
        this.selectedLocationId = Number(this.userData['locationId']);
      }

      this.checkRouteAndUpdateDropdown();
      this.cdr.markForCheck();
    }
  }

  private loadOrganizationsWithLocations(): void {
    this.cacheService
      .get('organizations-with-locations', this.authService.getOrganizationsWithLocations())
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        if (response?.data) {
          this.organizationList = response.data;
          this.checkRouteAndUpdateDropdown();
          this.cdr.markForCheck();
        }
      });
  }

  private isLocationInCurrentOrganization(locationId: number): boolean {
    const organizationList = this.organizationList;

    if (!organizationList || organizationList.length === 0) {
      return false;
    }

    const currentOrg = organizationList.find((org) => org['id'] === this.selectedOrganizationId);

    if (!currentOrg) {
      return false;
    }

    const locations = currentOrg['locations'] as Array<Record<string, unknown>> | undefined;
    return locations?.some((loc) => loc['id'] === locationId) ?? false;
  }

  get isAdmin(): boolean {
    return this.userData?.userType === UserType.Admin;
  }


  get companyName(): string {
    return this.userData?.organizationName ?? this.userData?.org_name ?? '';
  }

  onCompanyLogoError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
    this.companyLogo = null;
    this.cdr.markForCheck();
  }

  connectToXero(): void {
    this.xeroConnecting = true;
    this.xeroService.getAuthUrl().subscribe({
      next: (response) => {
        if (response.success && response.authUrl) {
          globalThis.location.href = response.authUrl;
        }
      },
      error: () => {
        this.xeroConnecting = false;
        this.cdr.markForCheck();
      },
    });
  }

  onSearch(searchText: string): boolean {
    if (!searchText) {
      this.menuItems = [];
      return false;
    }

    const normalizedSearch = searchText.toLowerCase().trim();
    this.menuItems = this.searchMenuItems(normalizedSearch);
    return true;
  }

  clearSearch(): void {
    this.searchText = '';
    this.menuItems = [];
  }

  openAccountDialog(): void {
    const userId = this.userData?.id ?? this.userData?.userId ?? 0;

    this.dialogService
      .open({
        ...AccountDialogConfigs.EDIT_ACCOUNT,
        data: {
          userId: userId,
        },
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result?.success) {
            this.getUserData();
            this.cdr.markForCheck();
          }
        },
        error: () => {},
      });
  }

  onViewAllNotifications(): void {
    this.router.navigate(['notifications/template-notification']);
  }

  onLogout(): void {
    this.authService.logout().subscribe({
      next: (response) => {
        if (response?.logoutUrl) {
          globalThis.location.href = response.logoutUrl;
        } else {
          this.router.navigate(['/login']);
        }
      },
      error: () => {
        this.router.navigate(['/login']);
      },
    });
  }

  getUserInitials(): string {
    const name = this.userData?.name;
    if (!name) {
      return 'U';
    }

    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  }

  get hasProfileImage(): boolean {
    const image = this.userData?.profileImage;

    if (!image || typeof image !== 'string') {
      return false;
    }

    const trimmedImage = image.trim();

    if (!trimmedImage) {
      return false;
    }

    const invalidGuidPattern = /^[0-]+$/;
    if (invalidGuidPattern.test(trimmedImage.replace(/-/g, ''))) {
      return false;
    }

    const emptyGuidPattern = /^0{8}-0{4}-0{4}-0{4}-0{12}$/;
    if (emptyGuidPattern.test(trimmedImage)) {
      return false;
    }

    const validImagePattern = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i;
    const isUrl = /^(https?:\/\/|\/|\.\/|\.\.\/)/i.test(trimmedImage);
    const hasImageExtension = validImagePattern.test(trimmedImage);
    const isBase64 = trimmedImage.startsWith('data:image/');

    return isUrl || hasImageExtension || isBase64;
  }

  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.style.display = 'none';

    const parent = imgElement.parentElement;
    if (parent && !parent.querySelector('.user-initials-fallback')) {
      const initialsSpan = document.createElement('span');
      initialsSpan.className = 'user-avatar user-initials-fallback';
      initialsSpan.textContent = this.getUserInitials();
      parent.appendChild(initialsSpan);
    }
  }

  private initializeUserData(): void {
    this.userData = this.storageService.getUserData();
    if (this.userData) {
      this.selectedOrganizationId = Number(this.userData['organizationId']);

      const storedLocationId = localStorage.getItem(SELECTED_LOCATION_KEY);
      if (storedLocationId) {
        this.selectedLocationId = Number(storedLocationId);
      }
    }

    this.initializeDropdownConfig();
  }

  private subscribeToUserChanges(): void {
    this.authService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user) {
        this.userData = user;
        this.selectedOrganizationId = Number(user.organizationId);
        this.loadOrganizationsWithLocations();
        this.cdr.markForCheck();
      }
    });
  }

  private subscribeToCompanyLogo(): void {
    this.companyLogoService.logo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((logo) => {
        this.companyLogo = logo;
        this.cdr.markForCheck();
      });
    this.companyLogoService.fetchLogo();
  }

  private subscribeToNavItems(): void {
    this.navServices.items.pipe(takeUntil(this.destroy$)).subscribe((menuItems) => {
      this.items = menuItems;
    });
  }

  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkRouteAndUpdateDropdown();
      });
  }

  private checkRouteAndUpdateDropdown(): void {
    const currentUrl = this.router.url;
    this.isFinancialManagementRoute = currentUrl.includes(FINANCIAL_MANAGEMENT_ROUTE);

    if (this.isFinancialManagementRoute) {
      this.initializeLocationDropdown();
    } else {
      this.initializeOrganizationDropdown();
    }

    this.cdr.markForCheck();
  }

  private initializeDropdownConfig(): void {
    this.initializeOrganizationDropdown();
  }

  private initializeOrganizationDropdown(): void {
    const organizationList = this.organizationList;

    if (!organizationList || organizationList.length === 0) {
      this.dropdownConfig = {
        id: 'organizationDropdown',
        controlType: 'select',
        formControlName: 'organizationId',
        config: {
          label: '',
          placeholder: 'Select Organization',
          required: true,
          showErrors: false,
          data: [],
          valuePrimitive: true,
          valueField: 'id',
          textField: 'name',
          value: null,
        },
        validation: {
          required: true,
          errorMessages: {
            required: 'Organization is required',
          },
        },
        gridPosition: { colSpan: 6 },
      };
      return;
    }

    const selectedOrg = organizationList.find((org) => org['id'] === this.selectedOrganizationId);

    this.dropdownConfig = {
      id: 'organizationDropdown',
      controlType: 'select',
      formControlName: 'organizationId',
      config: {
        label: '',
        placeholder: 'Select Organization',
        required: true,
        showErrors: false,
        data: organizationList,
        valuePrimitive: true,
        valueField: 'id',
        textField: 'name',
        value: (selectedOrg?.['id'] as number) ?? null,
      },
      validation: {
        required: true,
        errorMessages: {
          required: 'Organization is required',
        },
      },
      gridPosition: { colSpan: 6 },
    };
  }

  private initializeLocationDropdown(): void {
    const groupedLocations = this.transformToGroupedLocations();

    if (groupedLocations.length === 0) {
      this.dropdownConfig = {
        id: 'locationDropdown',
        controlType: 'select',
        formControlName: 'locationId',
        config: {
          label: '',
          placeholder: 'Select Location',
          required: true,
          showErrors: false,
          data: [],
          valuePrimitive: true,
          valueField: 'Id',
          textField: 'Name',
          groupField: 'OrganizationName',
          value: null,
        },
        validation: {
          required: true,
          errorMessages: {
            required: 'Location is required',
          },
        },
        gridPosition: { colSpan: 6 },
      };
      return;
    }

    const locationId = this.resolveLocationId(groupedLocations);

    this.dropdownConfig = {
      id: 'locationDropdown',
      controlType: 'select',
      formControlName: 'locationId',
      config: {
        label: '',
        placeholder: 'Select Location',
        required: true,
        showErrors: false,
        data: groupedLocations,
        valuePrimitive: true,
        valueField: 'Id',
        textField: 'Name',
        groupField: 'OrganizationName',
        value: locationId,
      },
      validation: {
        required: true,
        errorMessages: {
          required: 'Location is required',
        },
      },
      gridPosition: { colSpan: 6 },
    };

    if (locationId) {
      this.selectedLocationId = locationId;
    }
  }

  private resolveLocationId(groupedLocations: GroupedLocation[]): number | null {
    const storedFromLocalStorage = localStorage.getItem(SELECTED_LOCATION_KEY);
    let locationId: number | null = null;

    if (storedFromLocalStorage) {
      locationId = Number(storedFromLocalStorage);
      // Check if location exists AND belongs to the current organization
      const location = groupedLocations.find((loc) => loc.Id === locationId);
      if (!location || location.OrganizationId !== this.selectedOrganizationId) {
        locationId = null;
        localStorage.removeItem(SELECTED_LOCATION_KEY);
      }
    }

    return locationId ?? this.selectedLocationId ?? this.getFirstAvailableLocationId();
  }

  private transformToGroupedLocations(): GroupedLocation[] {
    const groupedData: GroupedLocation[] = [];
    const organizationList = this.organizationList;

    if (!organizationList || organizationList.length === 0) {
      return groupedData;
    }

    for (const org of organizationList) {
      const locations = org['locations'] as Array<Record<string, unknown>> | undefined;
      if (locations?.length) {
        for (const location of locations) {
          groupedData.push({
            Id: location['id'] as number,
            Name: location['name'] as string,
            OrganizationName: org['name'] as string,
            OrganizationId: org['id'] as number,
          });
        }
      }
    }

    return groupedData;
  }

  private getFirstAvailableLocationId(): number | null {
    // Get locationId from the token/userData
    if (this.userData?.['locationId']) {
      return Number(this.userData['locationId']);
    }

    return null;
  }

  private findOrganizationByLocationId(locationId: number): OrganizationLocationInfo | null {
    const organizationList = this.organizationList;

    if (!organizationList || organizationList.length === 0) {
      return null;
    }

    for (const org of organizationList) {
      const locations = org['locations'] as Array<Record<string, unknown>> | undefined;
      if (locations) {
        const location = locations.find((loc) => loc['id'] === locationId);
        if (location) {
          return {
            organizationId: org['id'] as number,
            organizationName: org['name'] as string,
            locationId: location['id'] as number,
            locationName: location['name'] as string,
          };
        }
      }
    }
    return null;
  }

  private handleOrganizationChangeSuccess(response: any): void {
    this.setActiveURL();
    const res = response?.item1;
    // Refresh Xero status for the new organization
    if (this.isAdmin) {
      this.xeroStatus$ = this.xeroService.getConnectionStatus();
      this.cdr.markForCheck();
    }
    this.authService.handleAuthCallback(res?.token, res?.refreshToken, '', true);
  }

  private setActiveURL(): void {
    const cleanRoute = this.router.url
      .split('/')
      .filter((segment) => segment && !/^\d+$/.test(segment))
      .join('/');

    sessionStorage.setItem('auth_return_url', '/' + cleanRoute || '/');
  }

  private initializeXeroStatus(): void {
    this.xeroStatus$ = this.xeroService.getConnectionStatus();
    this.cdr.markForCheck();
  }

  private searchMenuItems(searchText: string): Menu[] {
    const results: Menu[] = [];

    for (const menuItem of this.items ?? []) {
      if (this.matchesSearch(menuItem, searchText)) {
        results.push(menuItem);
      }
      this.searchChildItems(menuItem.children, searchText, results);
    }

    return results;
  }

  private matchesSearch(item: Menu, searchText: string): boolean {
    if (!item?.title) {
      return false;
    }
    const titleLower = item.title.toLowerCase();
    return item.type === 'link' && titleLower.includes(searchText);
  }

  private searchChildItems(
    children: Menu[] | undefined,
    searchText: string,
    results: Menu[]
  ): void {
    if (!children) {
      return;
    }

    for (const child of children) {
      if (this.matchesSearch(child, searchText)) {
        results.push(child);
      }
      this.searchChildItems(child.children, searchText, results);
    }
  }
}
